import { useState, useCallback, useRef, useEffect, useMemo, ReactNode } from 'react';
import cn from 'classnames';
import AsyncSelect from 'react-select/async';
import { components as RSComponents, ControlProps, StylesConfig } from 'react-select';
import { useSelector } from 'react-redux';
import { useField, useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';

import { useHttp } from 'hooks/http.hook';
import Label from 'components/InputsField/Label';
import InputError from 'components/InputsField/InputError';
import InputNote from 'components/InputsField/InputNote';
import MapPickerModal from 'components/common/MapPickerModal';
import {
  MAPBOX_FORWARD_GEOCODING_URL,
  formatMapboxFeatures,
  enrichAddressWithReverseGeocode,
} from 'utils/mapbox';

import styles from './styles.module.scss';

const MIN_SYMBOL = 1;
const MAPBOX_ACCESS_TOKEN = import.meta.env.VITE_MAP_API_TOKEN;
const MAPBOX_DEBOUNCE_DELAY = 400;

type AnyOption = any;

interface CityAutoCompleteSelectProps {
  url?: string;
  name: string;
  label?: string | ReactNode;
  /** Подпись над полем, как у остальных инпутов формы, а не внутри рамки */
  labelOutside?: boolean;
  required?: boolean;
  className?: string;
  selectClassName?: string;
  fieldClassName?: string;
  errorMode?: string;
  note?: string | ReactNode;
  noOptionsMessage?: string;
  useMapboxSearch?: boolean;
  mapboxLimit?: number;
  withMapPicker?: boolean;
  additionalAction?: ReactNode;
  onAddressChange?: (option: AnyOption) => void;
  styles?: StylesConfig<AnyOption, false>;
  icon?: string;
  labelText?: string;
  labelClassName?: string;
  disabled?: boolean;
  [key: string]: any;
}

const CityAutoCompleteSelect = ({
  url = '',
  name,
  label = '',
  labelOutside = false,
  required = false,
  className = '',
  selectClassName = '',
  fieldClassName,
  errorMode,
  note,
  noOptionsMessage = '',
  useMapboxSearch = false,
  mapboxLimit = 5,
  withMapPicker = false,
  additionalAction = null,
  onAddressChange,
  styles: extraStyles,
  icon,
  labelText,
  labelClassName,
  disabled,
  ...props
}: CityAutoCompleteSelectProps) => {
  const { t } = useTranslation();
  const { request } = useHttp();
  const { setFieldValue, setFieldTouched } = useFormikContext();
  const [field, meta] = useField({ name });

  const lang = useSelector((state: any) => state.settings.lang);
  const [isLoading, setIsLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const canUseMapPicker = Boolean(withMapPicker && useMapboxSearch && MAPBOX_ACCESS_TOKEN);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mapboxAbortRef = useRef<AbortController | null>(null);

  const hasError = Boolean(meta.touched && meta.error);

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      if (mapboxAbortRef.current) {
        mapboxAbortRef.current.abort();
      }
    };
  }, []);

  const loadOptions = (searchValue: string, callback: (options: AnyOption[] | null) => void) => {
    setInputValue(searchValue);
    if (searchValue.length <= MIN_SYMBOL) {
      callback(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    if (useMapboxSearch) {
      if (!MAPBOX_ACCESS_TOKEN) {
        console.warn('Missing Mapbox access token');
        callback([]);
        setIsLoading(false);
        return;
      }

      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      if (mapboxAbortRef.current) {
        mapboxAbortRef.current.abort();
      }

      searchTimeoutRef.current = setTimeout(() => {
        const params = new URLSearchParams({
          q: searchValue,
          access_token: MAPBOX_ACCESS_TOKEN,
          language: lang,
          limit: String(mapboxLimit),
          types: 'country,postcode,place,locality,neighborhood,address',
        });

        mapboxAbortRef.current = new AbortController();

        fetch(`${MAPBOX_FORWARD_GEOCODING_URL}?${params.toString()}`, {
          signal: mapboxAbortRef.current.signal,
        })
          .then((response) => response.json())
          .then((result) => {
            if (result?.features?.length) {
              callback(formatMapboxFeatures(result.features));
              return;
            }
            callback([]);
          })
          .catch((error) => {
            if (error?.name !== 'AbortError') {
              callback([]);
            }
          })
          .finally(() => {
            setIsLoading(false);
          });
      }, MAPBOX_DEBOUNCE_DELAY);

      return;
    }

    request(`${url}?q=${inputValue}`)
      .then((result: any) => {
        if (result.data.length) {
          const formattedOptions = result.data.map((item: any) => ({
            value: item.id,
            label: `${item.code}${item.post_code ? ` ${item.post_code}` : ''} ${item[`title_${lang}`]}`,
          }));
          callback(formattedOptions);
        } else {
          callback([]);
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const processAddressSelection = useCallback(
    async (option: AnyOption) => {
      let finalOption = option;
      if (option?.value?.location && !option?.value?.post_code) {
        finalOption = await enrichAddressWithReverseGeocode(option, lang);
      }
      setFieldValue(name, finalOption);
      onAddressChange?.(finalOption);
    },
    [name, setFieldValue, lang, onAddressChange]
  );

  const handleInputChange = useCallback(
    async (selectedOption: AnyOption) => {
      if (!useMapboxSearch) {
        setFieldValue(name, selectedOption);
        onAddressChange?.(selectedOption);
        return;
      }
      await processAddressSelection(selectedOption);
    },
    [useMapboxSearch, processAddressSelection, name, setFieldValue, onAddressChange]
  );

  const handleBlur = useCallback(() => {
    setInputValue('');
    setIsFocused(false);
    setFieldTouched(name);
  }, [name, setFieldTouched]);

  const handleFocus = useCallback(() => {
    setIsFocused(true);
  }, []);

  const handleMapSelection = useCallback(
    async (option: AnyOption) => {
      await processAddressSelection(option);
      setFieldTouched(name, true, false);
      setIsPickerOpen(false);
    },
    [processAddressSelection, name, setFieldTouched]
  );

  const customStyles: StylesConfig<AnyOption, false> = {
    ...(extraStyles || {}),
    menu: (provided, state) => {
      const base = { ...provided, zIndex: 9999 };
      if (extraStyles?.menu) {
        return { ...base, ...extraStyles.menu(provided, state) };
      }
      return base;
    },
    // Меню уходит в body (см. menuPortalTarget), там его нужно поднять над оверлеем модалки
    menuPortal: (provided, state) => {
      const base = { ...provided, zIndex: 9999 };
      if (extraStyles?.menuPortal) {
        return { ...base, ...extraStyles.menuPortal(provided, state) };
      }
      return base;
    },
  };

  const customComponents = useMemo(
    () => ({
      DropdownIndicator: null,
      ...(icon && {
        Control: ({ children, ...controlProps }: ControlProps<AnyOption, false>) => (
          <RSComponents.Control {...controlProps}>
            <img src={icon} alt="" className={styles.icon} />
            {children}
          </RSComponents.Control>
        ),
      }),
    }),
    [icon]
  );

  return (
    <div className={cn(className, styles.wrapper)}>
      {label && labelOutside && <Label name={name} label={label as string} required={required} />}
      <div
        className={cn(styles.field, fieldClassName, {
          [styles.fieldOutsideLabel]: labelOutside,
          [styles.fieldFocused]: isFocused,
          [styles.fieldError]: hasError,
          [styles.fieldDisabled]: disabled,
        })}
      >
        {label && !labelOutside && (
          <div className={styles.labelRow}>
            <label htmlFor={name} className={styles.label}>
              {label}
            </label>
            {required && <i className={styles.required}>*</i>}
          </div>
        )}
        {labelText && (
          <label htmlFor={name} className={labelClassName}>
            {labelText}
          </label>
        )}
        <AsyncSelect
          cacheOptions
          isLoading={isLoading}
          onBlur={handleBlur}
          onFocus={handleFocus}
          isDisabled={disabled}
          value={field.value && field.value}
          getOptionLabel={(option: AnyOption) => {
            if (canUseMapPicker) {
              return option.value ? option.value.full_address : option.full_address;
            }
            return option.label;
          }}
          loadingMessage={() => t('loading')}
          noOptionsMessage={() => (inputValue.length > MIN_SYMBOL ? noOptionsMessage : null)}
          placeholder={t('forms.startEnter')}
          classNamePrefix="select"
          loadOptions={loadOptions}
          onChange={handleInputChange}
          components={customComponents}
          // Иначе список городов обрезается по границе модалки или другого блока с overflow
          menuPortalTarget={document.body}
          menuPosition="fixed"
          {...props}
          styles={customStyles}
          className={cn(styles.select, selectClassName)}
        />
      </div>
      {hasError && <InputError message={meta.error as string} mode={errorMode || 'top-right'} />}
      {note && (
        <div className={styles.note}>
          <InputNote note={note} />
        </div>
      )}

      {(canUseMapPicker || additionalAction) && (
        <div className={styles.actionsRow}>
          {canUseMapPicker && (
            <button type="button" className={styles.pickButton} onClick={() => setIsPickerOpen(true)}>
              <img src={new URL('../../../../img/pick-on-map.svg', import.meta.url).href} alt="Карта" />
              {t('forms.city.pick_on_map')}
            </button>
          )}
          {additionalAction}
        </div>
      )}

      {canUseMapPicker && (
        <MapPickerModal
          isOpen={isPickerOpen}
          onClose={() => setIsPickerOpen(false)}
          onSelect={handleMapSelection}
          initialValue={field.value}
          lang={lang}
          labels={{
            title: t('forms.city.pick_on_map_modal_title'),
            helper: t('forms.city.pick_on_map_instructions'),
            confirm: t('forms.city.pick_on_map_confirm'),
            cancel: t('forms.city.pick_on_map_cancel'),
            error: t('forms.city.pick_on_map_error'),
          }}
        />
      )}
    </div>
  );
};

export default CityAutoCompleteSelect;
